
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface TranslationResponse {
  success: boolean;
  translatedCount?: number;
  successCount?: number;
  failedCount?: number;
  failedModules?: Array<{
    module: number;
    part: number;
    title: string;
    retryCount: number;
  }>;
  message?: string;
  error?: string;
  courseId?: string;
}

export const useTranslateCourseContent = (
  courseId?: string, 
  contentType: 'modules' | 'questions' | 'both' = 'both'
) => {
  return useMutation({
    mutationFn: async (): Promise<TranslationResponse> => {
      console.log('Starting course content translation for course:', courseId, 'contentType:', contentType);
      
      const { data, error } = await supabase.functions.invoke('translate-course-content', {
        body: { 
          courseId: courseId,
          contentType: contentType
        }
      });

      if (error) {
        console.error('Translation error:', error);
        throw new Error(`Translation failed: ${error.message}`);
      }

      console.log('Translation response:', data);
      return data as TranslationResponse;
    },
    onSuccess: (data) => {
      if (data.success) {
        const successMessage = data.failedModules && data.failedModules.length > 0 
          ? `Translation completed with some issues. ${data.successCount} items succeeded, ${data.failedCount} items failed.`
          : `Translation completed successfully! ${data.translatedCount} items translated.`;
        
        toast.success(successMessage);
        
        if (data.failedModules && data.failedModules.length > 0) {
          console.warn('Failed items:', data.failedModules);
          toast.warning(`${data.failedCount} items failed to translate. Check console for details.`);
        }
      } else {
        toast.error(`Translation failed: ${data.error}`);
      }
    },
    onError: (error) => {
      console.error('Translation error:', error);
      // No toast - progress indicator shows status, user can resume if needed
    },
  });
};
